/**
 * PageBuilderSettings — gear-icon settings surface for managed pages,
 * matching the project / category / topic settings pattern.
 *
 * Uses SettingsLayout (left sidebar of sections, breadcrumb header,
 * back button) and one component per section. DocumentsPanel and
 * ImagesPanel are shared with topic / category settings;
 * VersionHistoryPanel and ConversationHistoryPanel are page-specific.
 */
import { useCallback, useEffect, useState } from 'react';
import PathChips from '../components/common/PathChips';
import { isNestedSiteType } from '../components/common/pathRules';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useShowInternals } from '../contexts/InternalsContext';
import { keepReturnTo } from '../components/common/returnPath';
import api, { NULL_REF_ID } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import SettingsLayout, {
  SettingsSection,
} from '../components/settings/SettingsLayout';
import DocumentsPanel from '../components/DocumentsPanel';
import ImagesPanel from '../components/ImagesPanel';
import VersionHistoryPanel from '../components/VersionHistoryPanel';
import ConversationHistoryPanel from '../components/ConversationHistoryPanel';
import type {
  ManagedPage,
  ManagedPageUpdate,
  PageBuildStatus,
  PageStyle,
  ManagedSite,
  DeploymentPlan,
} from '../types';

type Section =
  | 'general'
  | 'deployment'
  | 'editorial'
  | 'documents'
  | 'images'
  | 'versions'
  | 'conversation'
  | 'danger';

const SECTIONS: SettingsSection[] = [
  { key: 'general', label: 'General' },
  { key: 'deployment', label: 'Deployment' },
  { key: 'editorial', label: 'Editorial guidelines' },
  { key: 'documents', label: 'Documents' },
  { key: 'images', label: 'Images' },
  { key: 'versions', label: 'Version history' },
  // The agent's raw transcript, tool calls and JSON arguments included. This is
  // the most literal answer in the app to "how did you come up with this", and
  // it sat on an ordinary route with no gate at all.
  { key: 'conversation', label: 'Conversation', internal: true },
  { key: 'danger', label: 'Danger zone', danger: true },
];

const STATUS_OPTIONS: { value: PageBuildStatus | ''; label: string }[] = [
  { value: '', label: 'No status' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'QUEUED', label: 'Queued' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'WAITING_FOR_USER_ACTION', label: 'Waiting for user' },
  { value: 'WAITING_FOR_SYSTEM_ACTION', label: 'Waiting for system' },
  { value: 'READY_TO_DEPLOY', label: 'Ready to deploy' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Canceled' },
  { value: 'SKIPPED', label: 'Skipped' },
  { value: 'ERROR', label: 'Error' },
];

const PAGE_STYLES: { value: PageStyle; label: string }[] = [
  { value: 'ARTICLE', label: 'Article' },
  { value: 'GUIDE', label: 'Guide' },
  { value: 'CHECKLIST', label: 'Checklist' },
  { value: 'LISTICLE', label: 'Listicle' },
  { value: 'FAQ', label: 'FAQ' },
  { value: 'COMPARISON', label: 'Comparison' },
  { value: 'REVIEWS', label: 'Reviews' },
  { value: 'PRODUCT_PAGE', label: 'Product page' },
  { value: 'PRICING_PAGE', label: 'Pricing page' },
  { value: 'SUPPORT_ARTICLE', label: 'Support article' },
  { value: 'DOCUMENTATION', label: 'Documentation' },
  { value: 'TOOL', label: 'Tool' },
  { value: 'FORUM_DISCUSSION', label: 'Forum discussion' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'TEMPLATE', label: 'Template' },
];

export default function PageBuilderSettings() {
  const { pageId: idParam, section: sectionParam } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageId = Number(idParam);
  const section = (sectionParam || 'general') as Section;
  const showInternals = useShowInternals();
  const visibleSections = SECTIONS.filter((s) => !s.internal || showInternals);

  const [page, setPage] = useState<ManagedPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(async () => {
    try {
      const p = await api.getManagedPage(pageId);
      setPage(p);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load page');
    } finally {
      setLoading(false);
    }
  }, [pageId]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // The section rail must not eat the way out. `section` is a route segment, so
  // navigating to it drops the query string — and with it the `?from=` that the
  // header's back button is pointing at. Looking around the sections before
  // heading back is the normal case, not a reason to strand someone.
  const handleSectionChange = (key: string) => {
    const qs = keepReturnTo(searchParams, new URLSearchParams()).toString();
    navigate(`/page-builder/${pageId}/settings/${key}` + (qs ? `?${qs}` : ''));
  };

  if (loading) {
    return (
      <div className="p-6">
        <LoadingSpinner />
      </div>
    );
  }
  if (error || !page) {
    return (
      <div className="p-6 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded">
        {error ?? 'Page not found'}
      </div>
    );
  }

  // The subject node this page came from, addressed directly.
  //
  // This used to build `/projects?project=…&category=…&topic=…&tab=pages` and
  // lean on the legacy redirect to translate it. Two things went wrong there.
  // `subjectPathFor` DISCARDS `tab` unless the node is a topic, in which case it
  // hardcodes `evidence` — so "back to the topic's pages" silently became the
  // keyword evidence pane, and `tab=pages` names a tab the workspace has not had
  // since it went to Evidence/Outputs. And it spells the node `category-N`,
  // which `parseNodeParam` resolves against LEGACY category ids; `category_id`
  // has carried the parent NODE's id since migration 108, so that lookup found
  // nothing and selected nothing. A plain node id is what the workspace writes
  // for itself.
  const backNodeId = page.topic_id ?? page.category_id;
  const backToSubject = page.project_id
    ? `/subjects/${page.project_id}${backNodeId ? `?node=${backNodeId}` : ''}`
    : '/subjects';
  const backToBuilder = `/page-builder/${pageId}`;

  const breadcrumb: { label: string; to?: string }[] = [
    { label: 'Subjects', to: '/subjects' },
  ];
  if (page.project_id) {
    breadcrumb.push({
      label: page.project_name ?? 'Subject',
      to: `/subjects/${page.project_id}`,
    });
  }
  if (page.category_id) {
    breadcrumb.push({
      label: page.category_name ?? 'Category',
      to: `/subjects/${page.project_id}?node=${page.category_id}`,
    });
  }
  if (page.topic_id) {
    breadcrumb.push({
      label: page.topic_name ?? 'Topic',
      to: `/subjects/${page.project_id}?node=${page.topic_id}`,
    });
  }
  breadcrumb.push({
    label: page.keyword_value || `Page ${page.id}`,
    to: backToBuilder,
  });
  breadcrumb.push({ label: 'Settings' });

  return (
    <SettingsLayout
      title={page.keyword_value || page.title || `Page ${page.id}`}
      scopeLabel="Page"
      breadcrumb={breadcrumb}
      backTo={backToBuilder}
      backLabel="Back to page builder"
      sections={visibleSections}
      activeSection={section}
      onSectionChange={handleSectionChange}
    >
      {section === 'general' && <GeneralSection page={page} onSaved={fetchPage} />}
      {section === 'deployment' && (
        <DeploymentSection page={page} onSaved={fetchPage} />
      )}
      {section === 'editorial' && (
        <EditorialSection page={page} onSaved={fetchPage} />
      )}
      {section === 'documents' && page.topic_keyword_id != null && (
        <DocumentsPanel
          topic_keyword_id={page.topic_keyword_id}
          label="Reference documents (this page's keyword)"
        />
      )}
      {section === 'images' && (
        <ImagesPanel page_id={page.id} label="Page images" />
      )}
      {section === 'versions' && (
        <VersionHistoryPanel
          pageId={page.id}
          currentVersionId={page.current_content_version_id}
          onRevert={fetchPage}
          defaultExpanded
        />
      )}
      {section === 'conversation' && showInternals && (
        <ConversationHistoryPanel pageId={page.id} />
      )}
      {section === 'danger' && (
        <DangerSection
          page={page}
          onDeleted={() => {
            // The page you were editing is gone, so the builder behind this
            // screen is gone too — the only sensible landing is the subject node
            // it belonged to.
            navigate(backToSubject);
          }}
        />
      )}
    </SettingsLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// General — page style, status, title, description, slug
// ─────────────────────────────────────────────────────────────────────────

function GeneralSection({
  page,
  onSaved,
}: {
  page: ManagedPage;
  onSaved: () => void;
}) {
  // '' means "no override — let the Angle phase decide". Derived from
  // page_style_source, NOT from page_style: the column is non-null (defaults
  // to ARTICLE), so a page the angle is free to claim still has a style
  // value. Reading the value made the control show ARTICLE-pinned on every
  // unpinned page, which is the opposite of the truth.
  const [pageStyle, setPageStyle] = useState<PageStyle | ''>(
    page.page_style_source === 'user' ? page.page_style || '' : ''
  );
  const [pageStatus, setPageStatus] = useState<PageBuildStatus | ''>(page.status || '');
  const [title, setTitle] = useState(page.title ?? '');
  const [description, setDescription] = useState(page.description ?? '');
  const [slug, setSlug] = useState(page.slug ?? '');
  const [path, setPath] = useState<string[]>(page.path_segments ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pinnedStyle = page.page_style_source === 'user' ? page.page_style : null;
  const dirty =
    (pageStyle || null) !== (pinnedStyle || null) ||
    (pageStatus || null) !== (page.status || null) ||
    title !== (page.title ?? '') ||
    description !== (page.description ?? '') ||
    slug !== (page.slug ?? '') ||
    path.join('/') !== (page.path_segments ?? []).join('/');

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: ManagedPageUpdate = {
        // null, NOT undefined: undefined is dropped from the JSON body, so
        // the field never reaches model_fields_set and clearing the override
        // silently did nothing. null is what tells the backend to unpin.
        page_style: (pageStyle || null) as PageStyle | null,
        status: (pageStatus || undefined) as PageBuildStatus | undefined,
        title,
        description,
        slug,
      };
      // Only when it CHANGED. Sending it stamps path_source="user", and doing
      // that on every save would silently pin a page nobody deliberately
      // moved — after which a rebuild could never correct it.
      if (path.join('/') !== (page.path_segments ?? []).join('/')) {
        payload.path_segments = path;
      }
      await api.updateManagedPage(page.id, payload);
      onSaved();
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">General</h2>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Page style override">
          <select
            value={pageStyle}
            onChange={(e) => setPageStyle(e.target.value as PageStyle)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Unset - let the Angle phase decide</option>
            {PAGE_STYLES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          {/* The Angle phase normally owns this — it's the step that has read
              the SERP and knows which shape of page has a wedge. Choosing here
              pins it, and later angle runs stop overwriting it. */}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {page.page_style_source === 'user' ? (
              <>
                Pinned to <strong>{page.page_style}</strong> by you - the Angle
                phase won&rsquo;t change it. Set to Unset to hand the decision
                back.
              </>
            ) : page.page_style_source === 'angle' ? (
              <>
                Unset, so the Angle phase owns it - it currently chose{' '}
                <strong>{page.page_style}</strong> from the SERP landscape, and
                a re-run may change it. Pick one here to pin it instead.
              </>
            ) : (
              <>
                Unset - the Angle phase will pick one, and with it the word
                targets and section bounds the writer aims for. Currently
                defaulting to <strong>{page.page_style}</strong>.
              </>
            )}
          </p>
        </Field>
        <Field label="Status">
          <select
            value={pageStatus || ''}
            onChange={(e) => setPageStatus(e.target.value as PageBuildStatus | '')}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value || 'none'} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Title">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
      </Field>

      <Field label="Description">
        <textarea
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
      </Field>

      <Field label="URL slug">
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="w-full px-3 py-2 font-mono border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
      </Field>

      {/* DIRECTLY UNDER THE SLUG, because together they are the URL — the
          slug alone reads like the whole answer, and it is the last segment. */}
      <Field label="Publish path">
        <PathChips
          segments={path}
          onChange={setPath}
          managedSiteId={page.managed_site_id ?? null}
          previewSlug={slug || page.slug || 'page-slug'}
          nested={isNestedSiteType(page.managed_site_type)}
        />
        {page.last_deployed_at && (
          <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
            This page is already published. Changing its path changes its URL -
            every existing link to the old one breaks.
          </p>
        )}
      </Field>

      <div className="text-xs text-gray-500 dark:text-gray-400 grid grid-cols-2 gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <div>
          <span className="font-medium">Created:</span>{' '}
          {new Date(page.created_at).toLocaleString()}
        </div>
        <div>
          <span className="font-medium">Updated:</span>{' '}
          {new Date(page.updated_at).toLocaleString()}
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || saving}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Deployment — managed site + deployment plan
// ─────────────────────────────────────────────────────────────────────────

function DeploymentSection({
  page,
  onSaved,
}: {
  page: ManagedPage;
  onSaved: () => void;
}) {
  const [siteId, setSiteId] = useState<number | typeof NULL_REF_ID>(
    page.managed_site_id ?? NULL_REF_ID,
  );
  const [planId, setPlanId] = useState<number | typeof NULL_REF_ID>(
    page.deployment_plan_id ?? NULL_REF_ID,
  );
  const [sites, setSites] = useState<ManagedSite[]>([]);
  const [plans, setPlans] = useState<DeploymentPlan[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Inline-create flows for site + deployment plan, matching the
  // "Create Managed Page" modal's UX so the user never has to leave
  // this view to seed a missing site/plan.
  const [creatingNewSite, setCreatingNewSite] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteRegisteredName, setNewSiteRegisteredName] = useState('');
  const [savingNewSite, setSavingNewSite] = useState(false);
  const [creatingNewPlan, setCreatingNewPlan] = useState(false);
  const [newPlanName, setNewPlanName] = useState('');
  const [savingNewPlan, setSavingNewPlan] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .getManagedSites()
      .then((s) => (live ? setSites(s) : null))
      .catch(() => null);
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (siteId === NULL_REF_ID || siteId == null) {
      setPlans([]);
      return;
    }
    let live = true;
    api
      .getDeploymentPlans({ managedSiteId: siteId, size: 100 })
      .then((res) => (live ? setPlans(res.items) : null))
      .catch(() => (live ? setPlans([]) : null));
    return () => {
      live = false;
    };
  }, [siteId]);

  const dirty =
    siteId !== (page.managed_site_id ?? NULL_REF_ID) ||
    planId !== (page.deployment_plan_id ?? NULL_REF_ID);

  const handleCreateNewSite = async () => {
    if (!newSiteName.trim() || !newSiteRegisteredName.trim()) return;
    setSavingNewSite(true);
    setError(null);
    try {
      const site = await api.createManagedSite({
        name: newSiteName.trim(),
        registered_name: newSiteRegisteredName.trim(),
      });
      setSites((prev) => [...prev, site]);
      setSiteId(site.id);
      // New site has no plans yet; clear any previously-selected plan.
      setPlanId(NULL_REF_ID);
      setCreatingNewSite(false);
      setNewSiteName('');
      setNewSiteRegisteredName('');
    } catch (e: any) {
      setError(e?.message ?? 'Failed to create site');
    } finally {
      setSavingNewSite(false);
    }
  };

  const handleCreateNewPlan = async () => {
    if (!newPlanName.trim() || siteId === NULL_REF_ID || siteId == null) return;
    setSavingNewPlan(true);
    setError(null);
    try {
      const plan = await api.createDeploymentPlan({
        name: newPlanName.trim(),
        managed_site_id: siteId as number,
      });
      setPlans((prev) => [...prev, plan]);
      setPlanId(plan.id);
      setCreatingNewPlan(false);
      setNewPlanName('');
    } catch (e: any) {
      setError(e?.message ?? 'Failed to create deployment plan');
    } finally {
      setSavingNewPlan(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: ManagedPageUpdate = {
        managed_site_id: siteId as number,
        deployment_plan_id: planId as number,
      };
      await api.updateManagedPage(page.id, payload);
      onSaved();
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Deployment</h2>
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Where this page belongs when it ships. Pick the managed site first; the
        deployment plans below are filtered to that site.
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Managed site
        </label>
        {creatingNewSite ? (
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={newSiteName}
                onChange={(e) => setNewSiteName(e.target.value)}
                placeholder="Site name"
                className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
              <input
                type="text"
                value={newSiteRegisteredName}
                onChange={(e) => setNewSiteRegisteredName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateNewSite()}
                placeholder="Domain (e.g. example.com)"
                className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCreateNewSite}
                disabled={
                  savingNewSite ||
                  !newSiteName.trim() ||
                  !newSiteRegisteredName.trim()
                }
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {savingNewSite ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreatingNewSite(false);
                  setNewSiteName('');
                  setNewSiteRegisteredName('');
                }}
                className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <select
              value={siteId === NULL_REF_ID ? '' : String(siteId)}
              onChange={(e) => {
                const next = e.target.value === '' ? NULL_REF_ID : Number(e.target.value);
                setSiteId(next);
                // Switching site invalidates the previously-selected plan.
                if (next !== siteId) setPlanId(NULL_REF_ID);
              }}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">No site selected</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.registered_name ? ` (${s.registered_name})` : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setCreatingNewSite(true)}
              className="px-2 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 whitespace-nowrap"
            >
              + New
            </button>
          </div>
        )}
      </div>

      {siteId !== NULL_REF_ID && siteId != null && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Deployment plan{' '}
            <span className="font-normal text-gray-400 dark:text-gray-500">(optional)</span>
          </label>
          {creatingNewPlan ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={newPlanName}
                onChange={(e) => setNewPlanName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateNewPlan()}
                placeholder="Plan name"
                className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
              <button
                type="button"
                onClick={handleCreateNewPlan}
                disabled={savingNewPlan || !newPlanName.trim()}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {savingNewPlan ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreatingNewPlan(false);
                  setNewPlanName('');
                }}
                className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <select
                value={planId === NULL_REF_ID ? '' : String(planId)}
                onChange={(e) =>
                  setPlanId(
                    e.target.value === '' ? NULL_REF_ID : Number(e.target.value),
                  )
                }
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">No plan selected</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setCreatingNewPlan(true)}
                className="px-2 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 whitespace-nowrap"
              >
                + New
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || saving}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Editorial guidelines — page-level, with topic-level shown read-only
// ─────────────────────────────────────────────────────────────────────────

function EditorialSection({
  page,
  onSaved,
}: {
  page: ManagedPage;
  onSaved: () => void;
}) {
  const [contentGuidelines, setContentGuidelines] = useState(
    page.content_guidelines ?? '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = contentGuidelines !== (page.content_guidelines ?? '');

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.updateManagedPage(page.id, {
        content_guidelines: contentGuidelines,
      });
      onSaved();
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Editorial guidelines
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          Pinned to every phase prompt below the topic-level rules. Use for
          tone, banned phrases, structure rules unique to this page.
        </p>
      </div>

      <Field label="Page-specific guidelines">
        <textarea
          rows={8}
          value={contentGuidelines}
          onChange={(e) => setContentGuidelines(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
      </Field>

      {page.topic_content_guidelines && (
        <div>
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Inherited topic-level guidelines (read-only)
          </div>
          <pre className="text-xs bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 whitespace-pre-wrap text-gray-700 dark:text-gray-300">
            {page.topic_content_guidelines}
          </pre>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            Edit topic-level guidelines on the topic settings page.
          </p>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || saving}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save guidelines'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Danger zone — delete page
// ─────────────────────────────────────────────────────────────────────────

function DangerSection({
  page,
  onDeleted,
}: {
  page: ManagedPage;
  onDeleted: () => void;
}) {
  const [confirm, setConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const expected = page.keyword_value || page.title || `Page ${page.id}`;
  const armed = confirm.trim() === expected;

  const handleDelete = async () => {
    if (!armed) return;
    setDeleting(true);
    setError(null);
    try {
      await api.deleteManagedPage(page.id);
      onDeleted();
    } catch (e: any) {
      setError(e?.message ?? 'Delete failed');
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <h2 className="text-base font-semibold text-red-700 dark:text-red-300">Danger zone</h2>
      <div className="border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 rounded p-4 space-y-3">
        <div>
          <div className="font-medium text-red-900 dark:text-red-200">Delete this page</div>
          <p className="text-sm text-red-800 dark:text-red-300 mt-1">
            Permanently removes the page and all its content versions,
            conversation history, and stored knowledge matches. The
            topic_keyword link survives but the page itself is gone.
          </p>
        </div>
        <Field label={`Type "${expected}" to confirm`}>
          <input
            type="text"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full text-sm border border-red-300 dark:border-red-700 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-red-500"
          />
        </Field>
        {error && (
          <div className="text-sm text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700 rounded px-3 py-2">
            {error}
          </div>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleDelete}
            disabled={!armed || deleting}
            className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 disabled:opacity-40"
          >
            {deleting ? 'Deleting…' : 'Delete page'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</span>
      {children}
    </label>
  );
}
